import type { PaletteMode } from '@mui/material'

import { primitives } from './primitives'

type StatusTokens = { bg: string; text: string; border: string; icon: string }

/**
 * Each key path in kebab case is a design system color name: `action.primary.bgHover` is
 * `action-primary-bg-hover`.
 */
export interface SemanticTokens {
  bg: {
    page: string
    surface: string
    subtle: string
    muted: string
    brand: string
    inverse: string
    avatar: string
    topbar: string
  }
  text: {
    primary: string
    secondary: string
    disabled: string
    inverse: string
    link: string
    accent: string
  }
  border: {
    subtle: string
    default: string
    strong: string
    control: string
    controlHover: string
    focus: string
    error: string
    inverse: string
  }
  action: {
    primary: { bg: string; bgHover: string; bgPressed: string; text: string }
    accent: { bg: string; bgHover: string; text: string }
    secondary: { bg: string; bgHover: string; border: string; text: string }
    ghost: { bgHover: string; bgActive: string; text: string }
    danger: { bg: string; bgHover: string; text: string }
    disabled: { bg: string; text: string }
    inverse: { bgHover: string; text: string; primaryBg: string; primaryBgHover: string }
  }
  status: { success: StatusTokens; warning: StatusTokens; error: StatusTokens; info: StatusTokens }
  elevation: { 1: string; 2: string; 3: string }
  focusRing: string
}

const { base, blue, celeste, yellow, neutral, green, red, alphaWhite } = primitives.color
const { elevation } = primitives.shadow

const focusRing = (gap: string, ring: string) => `0 0 0 2px ${gap}, 0 0 0 4px ${ring}`

export const semanticTokens: Record<PaletteMode, SemanticTokens> = {
  light: {
    bg: {
      page: neutral[50],
      surface: base.white,
      subtle: celeste[50],
      muted: celeste[100],
      brand: blue[900],
      inverse: blue[950],
      avatar: base.white,
      topbar: blue[950]
    },
    text: {
      primary: blue[950],
      secondary: celeste[700],
      disabled: celeste[500],
      inverse: base.white,
      link: blue[700],
      accent: blue[700]
    },
    border: {
      subtle: celeste[100],
      default: celeste[200],
      strong: celeste[300],
      control: celeste[500],
      controlHover: celeste[700],
      focus: blue[600],
      error: red[500],
      inverse: alphaWhite[12]
    },
    action: {
      primary: { bg: blue[900], bgHover: blue[800], bgPressed: blue[950], text: base.white },
      accent: { bg: yellow[300], bgHover: yellow[200], text: blue[950] },
      secondary: {
        bg: base.white,
        bgHover: celeste[50],
        border: celeste[300],
        text: blue[950]
      },
      ghost: { bgHover: celeste[100], bgActive: celeste[200], text: blue[950] },
      danger: { bg: red[600], bgHover: red[700], text: base.white },
      disabled: { bg: celeste[100], text: celeste[500] },
      inverse: {
        bgHover: alphaWhite[12],
        text: base.white,
        primaryBg: alphaWhite[12],
        primaryBgHover: alphaWhite[20]
      }
    },
    status: {
      success: { bg: green[50], text: green[700], border: green[200], icon: green[600] },
      warning: { bg: yellow[50], text: yellow[800], border: yellow[300], icon: yellow[500] },
      error: { bg: red[50], text: red[700], border: red[200], icon: red[500] },
      info: { bg: blue[50], text: blue[700], border: blue[200], icon: blue[600] }
    },
    elevation,
    focusRing: focusRing(base.white, blue[600])
  },
  dark: {
    bg: {
      page: celeste[950],
      surface: celeste[900],
      subtle: celeste[800],
      muted: celeste[800],
      brand: blue[800],
      inverse: blue[950],
      avatar: celeste[700],
      topbar: celeste[900]
    },
    text: {
      primary: celeste[50],
      secondary: celeste[300],
      disabled: celeste[600],
      inverse: base.white,
      link: blue[300],
      accent: blue[300]
    },
    border: {
      subtle: celeste[800],
      default: celeste[700],
      strong: celeste[600],
      control: celeste[600],
      controlHover: celeste[400],
      focus: blue[400],
      error: red[400],
      inverse: alphaWhite[12]
    },
    action: {
      primary: { bg: blue[600], bgHover: blue[500], bgPressed: blue[700], text: base.white },
      accent: { bg: yellow[300], bgHover: yellow[200], text: blue[950] },
      secondary: {
        bg: celeste[900],
        bgHover: celeste[800],
        border: celeste[600],
        text: celeste[50]
      },
      ghost: { bgHover: alphaWhite[8], bgActive: alphaWhite[16], text: celeste[50] },
      danger: { bg: red[600], bgHover: red[500], text: base.white },
      disabled: { bg: celeste[800], text: celeste[600] },
      inverse: {
        bgHover: alphaWhite[12],
        text: base.white,
        primaryBg: alphaWhite[12],
        primaryBgHover: alphaWhite[20]
      }
    },
    status: {
      success: { bg: green[950], text: green[300], border: green[800], icon: green[400] },
      warning: { bg: yellow[950], text: yellow[300], border: yellow[800], icon: yellow[400] },
      error: { bg: red[950], text: red[300], border: red[800], icon: red[400] },
      info: { bg: blue[900], text: blue[200], border: blue[700], icon: blue[400] }
    },
    elevation,
    focusRing: focusRing(celeste[900], blue[400])
  }
}
